import type { QrInfo, QrScan } from '../../shared/api.js';
import type { PruefpunktKey } from '../../shared/pruefpunkte.js';
import type { QrVariante } from '../../shared/qr-format.js';
import { api, ApiFehler, authKopf, fehlerText, token } from '../api.js';
import { abschnitt, ersetze, h, haengeAn, knopf, liste, meldung, schritte, setzeMeldung, zeile } from '../dom.js';
import { melde, uebernimmVomServer } from '../ergebnisse.js';
import type { Ansicht } from '../router.js';
import { holeStand, istAngemeldet } from '../sitzung.js';
import { lies } from '../speicher.js';
import { formatiereZeitpunkt } from '../zeit.js';
import { Aufraeumer, kopf, pruefpunktStand } from './gemeinsam.js';
import { hatZugang, zugangAbgelehnt, zugangsFormular } from './zugang.js';

const BEOBACHTUNGEN = [
  'Kamera erkennt keinen Code',
  'Zeigt nur Text, keinen Link',
  'Öffnet eine Websuche statt der Adresse',
  'Öffnet eine falsche Adresse',
  'Fehlermeldung im Browser',
];

/** Meldung von Hand, wenn die Kamera-App den Link nicht öffnet; dann kommt beim Server nichts an. */
function nichtGeoeffnet(key: PruefpunktKey, variante: string): HTMLElement {
  const name = `beobachtung-${key}`;
  const status = meldung();
  const notiz = h('input', { type: 'text', id: `${name}-notiz`, name: `${name}-notiz`, maxlength: 200, autocomplete: 'off' });
  return h(
    'details',
    { class: 'aufklapper' },
    h('summary', {}, `Kamera-App öffnet Aufkleber ${variante} nicht`),
    h(
      'fieldset',
      { class: 'feld' },
      h('legend', {}, 'Was ist passiert?'),
      ...BEOBACHTUNGEN.map((text, i) => h('label', { class: 'auswahl' }, h('input', { type: 'radio', name, value: text, checked: i === 0 }), text)),
    ),
    h('div', { class: 'feld' }, h('label', { for: `${name}-notiz` }, 'Notiz (optional)'), notiz),
    knopf(
      'Als nicht bestanden speichern',
      async () => {
        const gewaehlt = (document.querySelector(`input[name="${name}"]:checked`) as HTMLInputElement | null)?.value ?? BEOBACHTUNGEN[0] ?? '';
        await melde(key, 'fehler', `Kamera-App: ${gewaehlt}${notiz.value.trim() ? ` (${notiz.value.trim()})` : ''}`, { beobachtung: gewaehlt, notiz: notiz.value.trim() });
        setzeMeldung(status, 'Gespeichert.', 'ok');
      },
      'gefahr',
    ),
    status,
  );
}

export const qr: Ansicht = async (ziel) => {
  const aufraeumer = new Aufraeumer();
  const info = await lies('qr');
  ziel.append(
    ...kopf('QR-Aufkleber mit der Kamera-App', 'Öffnen die normalen Kamera-Apps einen Link ganz in Großbuchstaben sauber? Fehlerkorrektur Stufe Q, wie im Konzept.'),
    abschnitt(
      'So geht der Test',
      schritte(
        'Die Aufkleber drucken oder die Seite „QR-Aufkleber drucken“ an einem PC öffnen.',
        'Am Handy die normale Kamera-App öffnen (nicht diese App) und auf Aufkleber A richten.',
        'Den angezeigten Link antippen. Es öffnet sich der Browser mit dem Ergebnis.',
        'Dasselbe mit Aufkleber B (Vergleich, klein geschrieben).',
      ),
      h('p', {}, 'Am iPhone öffnet sich Safari, nicht die installierte App. Damit das Ergebnis diesem Gerät zugeordnet wird, dort einmal mit demselben Gerätenamen einrichten.'),
      h('a', { href: '/druck', class: 'knopf knopf-sekundaer' }, 'QR-Aufkleber anzeigen'),
    ),
    abschnitt('Aufkleber A: Großbuchstaben', info && h('p', { class: 'code', translate: 'no' }, info.linkGross), pruefpunktStand('qr-grossbuchstaben', aufraeumer), nichtGeoeffnet('qr-grossbuchstaben', 'A')),
    abschnitt('Aufkleber B: Vergleich', info && h('p', { class: 'code', translate: 'no' }, info.linkGemischt), pruefpunktStand('qr-vergleich', aufraeumer), nichtGeoeffnet('qr-vergleich', 'B')),
  );
  return aufraeumer.alles;
};

async function svgUrl(variante: QrVariante): Promise<string> {
  const antwort = await fetch(`/api/qr/aufkleber/${variante}`, { headers: await authKopf(), cache: 'no-store' });
  if (!antwort.ok) throw new ApiFehler(antwort.status, 'Aufkleber konnten nicht geladen werden.');
  return URL.createObjectURL(await antwort.blob());
}

interface QrTechnik {
  version: number;
  groesse: number;
  fehlerkorrektur: string;
  modi: string[];
}

const MODUS_TEXT: Record<string, string> = { Alphanumeric: 'kompakt (alphanumerisch)', Byte: 'Byte', Numeric: 'Ziffern' };

export const druck: Ansicht = async (ziel) => {
  if (!(await hatZugang())) {
    zugangsFormular(ziel, 'QR-Aufkleber für den Test');
    return;
  }
  const aufraeumer = new Aufraeumer();
  let info: QrInfo & { technikGross: QrTechnik; technikGemischt: QrTechnik };
  const urls: Partial<Record<QrVariante, string>> = {};
  // Blob-URLs immer freigeben, auch wenn der zweite Aufkleber nicht lädt.
  aufraeumer.dazu(() => Object.values(urls).forEach((u) => URL.revokeObjectURL(u)));
  try {
    info = await api<QrInfo & { technikGross: QrTechnik; technikGemischt: QrTechnik }>('/api/qr/info', { auswertung: true });
    urls.gross = await svgUrl('gross');
    urls.gemischt = await svgUrl('gemischt');
  } catch (fehler) {
    aufraeumer.alles();
    if (await zugangAbgelehnt(fehler)) return;
    ziel.append(h('h1', {}, 'QR-Aufkleber für den Test'), h('p', { class: 'meldung meldung-fehler' }, fehlerText(fehler)));
    return;
  }
  const alsGeraet = await istAngemeldet();

  const aufkleber = (variante: QrVariante, buchstabe: string, titel: string, link: string, technik: QrTechnik) =>
    h(
      'figure',
      { class: 'aufkleber' },
      h('p', { class: 'aufkleber-test' }, 'TEST'),
      h('img', { src: urls[variante] ?? '', alt: `QR-Code ${buchstabe}: ${link}`, class: 'aufkleber-gross', width: 240, height: 240 }),
      h('figcaption', {}, h('strong', {}, `${buchstabe} – ${titel}`), h('span', { class: 'code', translate: 'no' }, link), h('span', {}, `${technik.groesse} × ${technik.groesse} Punkte · Fehlerkorrektur ${technik.fehlerkorrektur} · ${technik.modi.map((m) => MODUS_TEXT[m] ?? m).join(', ')}`)),
      h('div', { class: 'aufkleber-klein' }, h('img', { src: urls[variante] ?? '', alt: '', width: 76, height: 76 }), h('span', {}, 'Aufklebergröße 2\u00a0cm')),
    );

  haengeAn(ziel, [
    alsGeraet && h('a', { href: '/', class: 'zurueck nicht-drucken' }, h('span', { 'aria-hidden': 'true' }, '← '), 'Übersicht'),
    h('h1', {}, 'QR-Aufkleber für den Test'),
    h('p', { class: 'nicht-drucken' }, 'Zum Testen ausdrucken oder an einem PC anzeigen und mit der Kamera-App des Handys scannen. Die Aufkleber tragen sichtbar „TEST“.'),
    knopf('Drucken', () => window.print(), 'primaer', { class: 'knopf knopf-primaer nicht-drucken' }),
    h(
      'div',
      { class: 'aufkleberbogen' },
      aufkleber('gross', 'A', 'Großbuchstaben', info.linkGross, info.technikGross),
      aufkleber('gemischt', 'B', 'Vergleich, klein geschrieben', info.linkGemischt, info.technikGemischt),
    ),
  ]);
  return aufraeumer.alles;
};

/** Ergebnisseite nach dem Scan mit der Kamera-App (läuft meist im Browser, nicht in der App). */
export const qrZiel: Ansicht = async (ziel, params, suche) => {
  const scanId = suche.get('scan');
  const status = meldung();
  ziel.append(h('h1', {}, 'QR-Aufkleber geöffnet'));
  if (!scanId || !/^[0-9a-f-]{36}$/i.test(scanId)) {
    ziel.append(h('p', {}, `Aufkleber ${params.kuerzel ?? ''}/${params.schluessel ?? ''} erkannt, aber ohne Scan-Kennung. Bitte mit der Kamera-App erneut scannen.`));
    return;
  }
  let scan: Omit<QrScan, 'ua' | 'geraetId'>;
  try {
    scan = await api<Omit<QrScan, 'ua' | 'geraetId'>>(`/api/qr/scans/${scanId}`, { modus: null });
  } catch (fehler) {
    ziel.append(h('p', { class: 'meldung meldung-fehler' }, fehlerText(fehler)));
    return;
  }
  const variante = scan.variante === 'gross' ? 'A – Großbuchstaben' : 'B – Vergleich, klein geschrieben';
  const zuordnung = h('div', {});
  ziel.append(
    h('p', { class: 'gross-ergebnis meldung meldung-ok' }, h('span', { 'aria-hidden': 'true' }, '✓ '), 'Die Kamera-App hat den Link geöffnet.'),
    abschnitt(
      'Angekommen',
      liste(
        zeile('Aufkleber', variante),
        zeile('Pfad wie angekommen', h('span', { class: 'code', translate: 'no' }, scan.pfadRoh)),
        zeile('Adresse wie angekommen', h('span', { class: 'code', translate: 'no' }, scan.hostRoh)),
        zeile('Unverändert', scan.pfadUnveraendert ? 'ja' : 'nein, Schreibweise verändert'),
        zeile('Zeit', formatiereZeitpunkt(scan.empfangenAm)),
      ),
    ),
    zuordnung,
    status,
  );

  if (!(await token())) {
    ersetze(
      zuordnung,
      abschnitt(
        'Noch keinem Gerät zugeordnet',
        h('p', {}, 'Der Scan ist gespeichert. Zum Zuordnen dieses Gerät hier im Browser einmal einrichten, mit demselben Namen wie in der App.'),
        h('a', { href: `/einrichtung?weiter=${encodeURIComponent(location.pathname + location.search)}`, class: 'knopf knopf-primaer' }, 'Gerät einrichten'),
      ),
    );
    return;
  }
  try {
    const antwort = await api<{ zugeordnet: boolean; ergebnis?: Parameters<typeof uebernimmVomServer>[0][number] }>(`/api/qr/scans/${scanId}/zuordnen`, { methode: 'POST' });
    if (antwort.ergebnis) await uebernimmVomServer([antwort.ergebnis]);
    await holeStand().catch(() => {});
    setzeMeldung(status, `Ergebnis gespeichert für „${(await lies('name')) ?? 'dieses Gerät'}“.`, 'ok');
    zuordnung.append(h('a', { href: '/pruefung/qr', class: 'knopf knopf-sekundaer' }, 'Zur Übersicht der QR-Prüfung'));
  } catch (fehler) {
    setzeMeldung(status, fehlerText(fehler), 'fehler');
  }
};
